<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[560px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-md tw-my-10 tw-rounded">
            <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-text-gray-600 tw-bg-gray-50">
                {{ isEdit ? $t('portal.categories.edit') : $t('portal.categories.create') }}
            </div>

            <div class="tw-p-5 tw-bg-white">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4">
                    <div class="tw-col-span-12 md:tw-col-span-8">
                        <FormField left-icon="solar:tag-bold-duotone" v-model="form.name" :label="$t('fields.name')" type="text" :error="err('name')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField v-model="form.status" :label="$t('fields.status')" type="select" :options="statusOptions" :error="err('status')" />
                    </div>
                    <div class="tw-col-span-12">
                        <FormField v-model="form.description" :label="$t('fields.description')" type="textarea" :error="err('description')" />
                    </div>
                </div>
            </div>

            <div class="tw-p-4 tw-border-t tw-justify-end tw-flex tw-items-center tw-gap-2 tw-bg-gray-50">
                <a-button :disabled="loading" @click="onCancel" intent="simple" class="tw-min-w-[120px]">{{ $t('buttons.cancel') }}</a-button>
                <a-button :disabled="loading" :loading="loading" @click="onSave" class="tw-min-w-[120px]">{{ isEdit ? $t('buttons.update') : $t('buttons.create') }}</a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import { create, update } from '@/api/categories';
import useAlert from '@/composables/useAlert';
import { useValidation } from '@/composables/useValidation';

const props = defineProps({
    visible: { type: Boolean, default: false },
    category: { type: Object, default: null },
});
const emit = defineEmits(['cancel', 'saved']);
const i18n = useI18n();

const loading = ref(false);
const errors = ref({});
const form = ref(defaults());
const err = useValidation(errors);
const isEdit = computed(() => !!props.category?.id);

const statusOptions = [
    { value: 'active', label: i18n.t('common.active') },
    { value: 'inactive', label: i18n.t('common.inactive') },
];

function defaults() {
    return { name: '', description: '', status: 'active' };
}

watch(() => props.visible, (v) => {
    if (v) {
        errors.value = {};
        form.value = props.category ? { ...defaults(), ...props.category } : defaults();
    }
});

const onCancel = () => { if (!loading.value) emit('cancel'); };

const onSave = async () => {
    loading.value = true;
    errors.value = {};
    try {
        const req = isEdit.value ? update(props.category.id, form.value) : create(form.value);
        const { data } = await req;
        if (data.code === 'SUCCESS') {
            useAlert(i18n.t(isEdit.value ? 'messages.updated-successfully' : 'messages.created-successfully', { field: i18n.t('portal.entities.category') }));
            emit('saved', data.category);
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) errors.value[k] = e.response.data.errors[k][0];
        }
    } finally {
        loading.value = false;
    }
};
</script>
